import React, { useState } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import CloseIcon from '@mui/icons-material/Close';
import { COLORS } from '../styles';

// Driver downloads bags off the inbound. Each flight has an expected count.
// Driver taps + or - to count, or simulates a scan.
function DownloadModeScreen({ driver, downloadCounts, onIncrement, onDecrement, onScan, onComplete }) {
  const flights = driver?.flights || [];
  const totalExpected = flights.reduce((t, f) => t + (f.bags || 0), 0);
  const totalGot = flights.reduce((t, f) => t + (downloadCounts[f.uniqueId] || 0), 0);
  const [viewedNote, setViewedNote] = useState(null);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Header */}
      <Box sx={{
        p: 1.5,
        backgroundColor: '#000',
        borderBottom: `2px solid ${COLORS.accent}`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontSize: '0.6rem', color: COLORS.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
              Download Mode
            </Typography>
            <Typography sx={{ fontSize: '1.1rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1 }}>
              {totalGot} / {totalExpected}
            </Typography>
          </Box>
          <Box sx={{
            backgroundColor: totalGot === totalExpected ? COLORS.success : COLORS.warn,
            color: '#0a1020',
            borderRadius: 999,
            px: 1.5, py: 0.25,
            fontSize: '0.6rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>
            {totalGot === totalExpected ? 'COMPLETE' : `${totalExpected - totalGot} TO GO`}
          </Box>
        </Box>
        <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, mt: 0.25 }}>
          Tap right side to add a bag · left side to remove
        </Typography>
      </Box>

      {/* Per-flight counter rows */}
      <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {flights.map((flight) => {
          const got = downloadCounts[flight.uniqueId] || 0;
          const expected = flight.bags || 0;
          const cat = flight.connectionCategory;
          const catColor =
            cat === 'hot' ? COLORS.hot :
            cat === 'cold' ? COLORS.cold :
            cat === 'priority' ? COLORS.warn :
            cat === 'freight-only' ? COLORS.freight :
            COLORS.textSecondary;
          const isDone = got >= expected;
          const bins = Object.entries(flight.binDistribution || {})
            .filter(([, v]) => v > 0)
            .map(([bin, count]) => ({ bin, count }));

          return (
            <Box
              key={flight.uniqueId}
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                borderBottom: `1px solid ${COLORS.divider}`,
                position: 'relative',
                opacity: isDone ? 0.85 : 1,
              }}
            >
              {/* MINUS half */}
              <Box
                onClick={() => onDecrement(flight.uniqueId)}
                sx={{
                  width: 50,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(244, 63, 94, 0.1)',
                  cursor: 'pointer',
                  '&:active': { backgroundColor: 'rgba(244, 63, 94, 0.3)' },
                }}
              >
                <RemoveIcon sx={{ color: COLORS.danger, fontSize: 20 }} />
              </Box>

              {/* Center info */}
              <Box sx={{
                flex: 1,
                p: 1.25,
                borderLeft: `3px solid ${catColor}`,
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '1.25rem',
                    color: COLORS.textPrimary,
                    fontWeight: 800,
                    lineHeight: 1.05,
                    letterSpacing: 0.3,
                  }}>
                    {flight.flightNumber.replace(/^[A-Z]{2}\s*/, '')} {flight.destination}
                  </Typography>
                  {bins.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.4, flexWrap: 'wrap' }}>
                      {bins.map(({ bin, count }) => (
                        <Box key={bin} sx={{
                          fontSize: '0.65rem',
                          fontWeight: 800,
                          color: '#0a1020',
                          backgroundColor: COLORS.accent,
                          px: 0.7,
                          py: 0.15,
                          borderRadius: 0.5,
                          lineHeight: 1.3,
                        }}>
                          BIN {bin}{count > 1 ? ` · ${count}` : ''}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                {/* Reserved slot so the count stays aligned across rows
                    whether or not a note exists. */}
                <Box sx={{ width: 32, display: 'flex', justifyContent: 'center' }}>
                  {flight.notes && (
                    <IconButton
                      onClick={(e) => { e.stopPropagation(); setViewedNote(flight); }}
                      size="small"
                      sx={{
                        color: COLORS.warn,
                        backgroundColor: 'rgba(251, 191, 36, 0.12)',
                        '&:hover': { backgroundColor: 'rgba(251, 191, 36, 0.25)' },
                        p: 0.5,
                      }}
                    >
                      <StickyNote2Icon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 56 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.25 }}>
                    <Typography sx={{
                      fontSize: '1.5rem',
                      color: isDone ? COLORS.success : COLORS.textPrimary,
                      fontWeight: 800,
                      lineHeight: 1,
                    }}>
                      {got}
                    </Typography>
                    <Typography sx={{ fontSize: '1rem', color: COLORS.textMuted, fontWeight: 600 }}>/</Typography>
                    <Typography sx={{
                      fontSize: '1.15rem',
                      color: COLORS.accent,
                      fontWeight: 800,
                      lineHeight: 1,
                    }}>
                      {expected}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.5rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, mt: 0.2 }}>
                    bags
                  </Typography>
                </Box>
              </Box>

              {/* PLUS half */}
              <Box
                onClick={() => onIncrement(flight.uniqueId)}
                sx={{
                  width: 50,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(34, 197, 94, 0.1)',
                  cursor: 'pointer',
                  '&:active': { backgroundColor: 'rgba(34, 197, 94, 0.3)' },
                }}
              >
                <AddIcon sx={{ color: COLORS.success, fontSize: 24 }} />
              </Box>

              {isDone && (
                <CheckCircleIcon sx={{
                  position: 'absolute',
                  top: 4, right: 60,
                  color: COLORS.success,
                  fontSize: 14,
                }} />
              )}
            </Box>
          );
        })}
      </Box>

      {/* Note overlay (tap a yellow note icon on a row to view) */}
      {viewedNote && (
        <Box
          onClick={() => setViewedNote(null)}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(4px)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            animation: 'noteFade 150ms ease-out',
            '@keyframes noteFade': {
              from: { opacity: 0 },
              to:   { opacity: 1 },
            },
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: 'rgba(15, 23, 42, 0.98)',
              border: `1px solid ${COLORS.warn}`,
              borderRadius: 2,
              p: 1.75,
              width: '100%',
              maxWidth: 280,
              position: 'relative',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
            }}
          >
            <IconButton
              size="small"
              onClick={() => setViewedNote(null)}
              sx={{
                position: 'absolute',
                top: 4, right: 4,
                color: COLORS.textMuted,
                p: 0.5,
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1 }}>
              <StickyNote2Icon sx={{ color: COLORS.warn, fontSize: 14 }} />
              <Typography sx={{
                fontSize: '0.55rem', color: COLORS.warn, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.6,
              }}>
                Dispatcher note
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.85rem', color: COLORS.textPrimary, fontWeight: 700, mb: 0.5 }}>
              {viewedNote.flightNumber} → {viewedNote.destination}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: COLORS.textPrimary, lineHeight: 1.4, fontStyle: 'italic' }}>
              {viewedNote.notes}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Footer action */}
      <Box sx={{ p: 1.5, borderTop: `1px solid ${COLORS.divider}` }}>
        <Button
          fullWidth
          onClick={onComplete}
          disabled={totalGot === 0}
          sx={{
            backgroundColor: totalGot === totalExpected ? COLORS.success : COLORS.warn,
            color: '#0a1020',
            fontWeight: 700,
            py: 1.25,
            fontSize: '0.85rem',
            '&:hover': { opacity: 0.9 },
            '&.Mui-disabled': { backgroundColor: COLORS.surfaceAlt, color: COLORS.textMuted },
          }}
        >
          {totalGot === totalExpected ? 'DOWNLOAD COMPLETE ▸' : `END DOWNLOAD (${totalGot}/${totalExpected}) ▸`}
        </Button>
      </Box>
    </Box>
  );
}

export default DownloadModeScreen;
